import { Component, OnInit } from '@angular/core';
import {
  DialogService,
  EventAggregator,
  SearchConfig,
  tr,
  Types,
  ViewType,
  KeyBase,
  DialogResult,
  OverviewMain,
} from '@kpd/framework-ngx';
import { Tenant } from 'app/shared/models/tenant';
import { TenantService } from 'app/shared/services/tenant.service';
import { catchError, switchMap } from 'rxjs/operators';
import { PermissionsetFacade } from 'app/shared/services/facades/permissionset.facade';
import { PermissionsetsNavigatorHelper } from 'app/permissionsets/permissionsets-navigator-helper';
import { of } from 'rxjs';
import { Permissions } from 'app/shared/services/permissions';
import { Permissionset } from 'app/shared/models/permissionset';
import { PermissionsetKey } from 'app/shared/models/keys/permissionset-key';

@Component({
  selector: 'app-permission-sets-overview',
  templateUrl: './permissionsets-overview.component.html',
})
export class PermissionsetsOverviewComponent extends OverviewMain implements OnInit {
  Permissions: typeof Permissions = Permissions;
  columnsConfig = this.createColumnsConfig();
  searchConfig = this.createPermissionsetSearchConfig();
  viewType = ViewType.Table;
  selectedItemIds: KeyBase[] = [];
  tenant: Tenant = new Tenant();
  ViewTypes = ViewType;

  constructor(
    public navigatorHelper: PermissionsetsNavigatorHelper,
    public entityFacade: PermissionsetFacade,
    public eventAggregator: EventAggregator,
    private dialogService: DialogService,
    private tenantService: TenantService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.tenantService.getTenant().subscribe((tenant: Tenant) => {
      this.tenant = tenant;
    });

    this.entityFacade.viewType$.subscribe(result => {
      this.viewType = result;
    });
  }

  onDelete(): void {
    if (this.selectedItemIds.some(id => (id as PermissionsetKey).isGlobalPermission)) {
      this.dialogService
        .open('alert', {
          message: tr('Global permissionsets can not be deleted'),
          header: tr('Delete failed'),
          type: 'error',
        })
        .afterClosed();
      return;
    }
    const message = tr('Are you sure to delete one (or more) selected permission set(s)?');
    this.dialogService
      .open('confirm', {
        message,
        header: tr('Delete permission set(s)'),
        verifyButtonText: tr('Delete'),
        verifyButtonStyle: 'danger',
        cancelButtonText: tr('Cancel'),
      })
      .afterClosed()
      .pipe(
        switchMap(result => {
          if (result === DialogResult.Verify) {
            return this.entityFacade.deleteItems(this.selectedItemIds);
          }
          return of({});
        }),
        catchError((error: any) => {
          if (error.status === 409 || error.status === 400) {
            return this.dialogService
              .open('confirm', {
                message: tr(`Cannot delete permission set due to conflict(s)`) + ` : ${error.error}`,
                header: tr('Delete failed'),
                hideVerify: true,
                cancelButtonText: tr('Ok'),
              })
              .afterClosed();
          }
          throw error;
        }),
      )
      .subscribe(() => {});
  }

  onAdd(): void {
    this.entityFacade.clearSelection();
    this.navigatorHelper.navigateToCreateFromDetail(true);
  }

  onToggleViewType(): void {
    this.entityFacade.changeViewType(this.viewType === ViewType.Table ? ViewType.Card : ViewType.Table);
  }

  onImport(): void {
    this.dialogService
      .open('wizard')
      .afterClosed()
      .subscribe(result => {
        if (result === DialogResult.Verify) {
          this.entityFacade.loadData();
        }
      });
  }

  createPermissionsetSearchConfig(): any {
    return new SearchConfig({
      multipleCriteria: true,
      fields: [
        { field: 'name', displayValue: tr('Name'), dataType: Types.String },
        { field: 'application', displayValue: tr('Application'), dataType: Types.String },
        { field: 'isKpdSet', displayValue: 'KPD set', dataType: Types.Boolean },
      ],
    });
  }

  createColumnsConfig(): any[] {
    return [
      { field: 'name', header: tr('Name'), width: '25%', type: 'link' },
      { field: 'translatedDescription', header: tr('Description'), width: '25%' },
      { field: 'application', header: tr('Application') },
      { field: 'type', header: 'Type', type: 'type', width: '200px' },
      { field: 'isKpdSet', header: 'KPD', type: 'KPD', width: '65px' },
    ];
  }

  hasAssign(item: Permissionset): boolean {
    return item.type === 'both' || item.type === 'assign';
  }

  hasUse(item: Permissionset): boolean {
    return item.type === 'both' || item.type === 'use';
  }
}
